// Fizban's Treasury of Dragons Sourcebook
export const FIZBANS_SOURCE = {
  id: 'fizbans',
  name: "Fizban's Treasury of Dragons",
  author: 'Wizards of the Coast',
  version: '1.0',
  description: 'Comprehensive guide to dragons including draconic races, subclasses, spells, and legendary dragon hoards',
  enabled: true,
  removable: true,
  builtin: true, // [CW-TRIO-SOURCES 2026-07-12]: shipped sourcebook — excluded from user-content listings
  categories: {
    items: true,
    spells: true,
    feats: true,
    backgrounds: false,
    subclasses: true,  // [FIX JAN 2026]: Enable subclass exposure
    abilities: true,
    boons: false
  },
  content: {
    items: {
      // Dragon-themed Magic Items
      'items_amethyst_lodestone': {
        weight: 1, size: [1, 1], baseValue: 20000, icon: '💜',
        name: 'Amethyst Lodestone',
        category: 'items',
        description: 'This gem can be used to craft items or as a component for summoning spells. Allows casting of sending once per day.',
        requiresAttunement: true,
        rarity: 'very rare',
        spells: [
          { name: 'Sending', charges: 1, level: 3 }
        ],
        charges: 1,
        maxCharges: 1,
        chargesPerDay: true,
        rechargeType: 'dawn'
      },
      'items_ascendant_dragon_touched_focus': {
        weight: 1, size: [1, 2], baseValue: 15000, icon: '🐲',
        name: 'Ascendant Dragon-Touched Focus',
        category: 'items',
        description: '+3 bonus to spell attack rolls and spell save DC. When you cast a damage spell, add 1d8 of the dragon damage type.',
        requiresAttunement: true,
        rarity: 'legendary',
        magicalBonus: 3
      },
      'items_bloodwell_vial_plus_3': {
        weight: 0.5, size: [1, 1], baseValue: 16000, icon: '🩸',
        name: 'Bloodwell Vial +3',
        category: 'items',
        description: '+3 to spell attacks and DC for sorcerer spells. Regain 5 sorcery points once per day.',
        requiresAttunement: true,
        rarity: 'very rare',
        magicalBonus: 3,
        charges: 1,
        maxCharges: 1,
        chargesPerDay: true,
        rechargeType: 'dawn'
      },
      'items_crystal_blade': {
        weight: 3, size: [1, 4], baseValue: 12000, icon: '💎',
        name: 'Crystal Blade',
        category: 'weapons',
        description: '+3 longsword that deals extra 1d8 radiant damage to dragons.',
        damage: '1d8',
        damageType: 'slashing',
        properties: ['Versatile (1d10)'],
        requiresAttunement: true,
        rarity: 'very rare',
        magicalBonus: 3
      },
      'items_dragon_scale_mail': {
        weight: 45, size: [2, 3], baseValue: 4000, icon: '🐉',
        name: 'Dragon Scale Mail',
        category: 'armor',
        description: '+1 scale mail made from dragon scales. Resistance to one damage type based on dragon type.',
        ac: 14,
        maxDexMod: 2,
        armorType: 'medium',
        requiresAttunement: true,
        rarity: 'very rare',
        magicalBonus: 1
      },
      'items_dragon_slayer': {
        weight: 3, size: [1, 4], baseValue: 8000, icon: '⚔️',
        name: 'Dragon Slayer Longsword',
        category: 'weapons',
        description: '+1 weapon that deals extra 3d6 damage to dragons.',
        damage: '1d8',
        damageType: 'slashing',
        properties: ['Versatile (1d10)'],
        requiresAttunement: false,
        rarity: 'rare',
        magicalBonus: 1
      },
      'items_dragon_touched_focus': {
        weight: 1, size: [1, 2], baseValue: 3000, icon: '🔮',
        name: 'Dragon-Touched Focus',
        category: 'items',
        description: '+1 to spell attack rolls and spell save DC. Can use a bonus action to transform into a pseudodragon.',
        requiresAttunement: true,
        rarity: 'uncommon',
        magicalBonus: 1
      },
      'items_dragon_vessel': {
        weight: 1, size: [1, 1], baseValue: 5000, icon: '🏺',
        name: 'Dragon Vessel',
        category: 'items',
        description: 'This vessel can be filled with liquid. Once per day, transform the liquid into a potion based on dragon type.',
        requiresAttunement: false,
        rarity: 'rare',
        charges: 1,
        maxCharges: 1,
        chargesPerDay: true,
        rechargeType: 'dawn'
      },
      'items_dragon_wing_bow': {
        weight: 2, size: [1, 4], baseValue: 7500, icon: '🏹',
        name: 'Dragon Wing Bow',
        category: 'weapons',
        description: '+2 longbow. When no ammunition, produces its own dragon bone arrows.',
        damage: '1d8',
        damageType: 'piercing',
        properties: ['Ammunition (150/600)', 'Heavy', 'Two-handed'],
        requiresAttunement: true,
        rarity: 'rare',
        magicalBonus: 2
      },
      'items_dragonhide_belt': {
        weight: 1, size: [2, 1], baseValue: 6000, icon: '🎗️',
        name: 'Dragonhide Belt',
        category: 'items',
        description: 'Increase Strength score by 2 to a maximum of 24. Advantage on Charisma checks with dragons.',
        equipSlot: 'waist',
        requiresAttunement: true,
        rarity: 'rare',
        statBonus: { strength: 2 }
      },
      'items_dragonlance': {
        weight: 6, size: [1, 5], baseValue: 10000, icon: '🔱',
        name: 'Dragonlance',
        category: 'weapons',
        description: '+3 lance. When used against dragons, deals extra 3d6 piercing and they have disadvantage on saves.',
        damage: '1d12',
        damageType: 'piercing',
        properties: ['Reach', 'Special'],
        requiresAttunement: true,
        rarity: 'legendary',
        magicalBonus: 3
      },
      'items_drakewarden_armor': {
        weight: 45, size: [2, 3], baseValue: 4500, icon: '🛡️',
        name: 'Drakewarden Armor',
        category: 'armor',
        description: '+1 scale mail. As an action, grow spectral dragon wings for 1 hour flight.',
        ac: 14,
        maxDexMod: 2,
        armorType: 'medium',
        requiresAttunement: true,
        rarity: 'rare',
        magicalBonus: 1,
        charges: 1,
        maxCharges: 1,
        chargesPerDay: true,
        rechargeType: 'dawn'
      },
      'items_emerald_pen': {
        weight: 0.1, size: [1, 1], baseValue: 8000, icon: '✒️',
        name: 'Emerald Pen',
        category: 'items',
        description: 'This pen never runs out of ink. Can cast illusory script at will.',
        requiresAttunement: true,
        rarity: 'uncommon',
        spells: [
          { name: 'Illusory Script', charges: 0, level: 1 }
        ]
      },
      'items_flail_of_tiamat': {
        weight: 2, size: [1, 2], baseValue: 60000, icon: '🐉',
        name: 'Flail of Tiamat',
        category: 'weapons',
        description: '+3 flail with 5 heads. Each head deals different dragon breath damage type.',
        damage: '1d8',
        damageType: 'bludgeoning',
        properties: [],
        requiresAttunement: true,
        rarity: 'artifact',
        magicalBonus: 3
      },
      'items_fizban_platinum_candle': {
        weight: 0.5, size: [1, 1], baseValue: 100000, icon: '🕯️',
        name: "Fizban's Platinum Candle",
        category: 'items',
        description: 'When lit, casts a wish spell. The candle is then destroyed.',
        requiresAttunement: false,
        rarity: 'legendary',
        charges: 1,
        maxCharges: 1,
        rechargeType: 'never'
      },
      'items_gem_dragon_focus': {
        weight: 1, size: [1, 1], baseValue: 10000, icon: '💎',
        name: 'Gem Dragon Focus',
        category: 'items',
        description: 'Can be used as a spellcasting focus. +2 to spell attacks and DC. Telepathy 30 feet.',
        requiresAttunement: true,
        rarity: 'very rare',
        magicalBonus: 2
      },
      'items_gold_canary_figurine': {
        weight: 0.1, size: [1, 1], baseValue: 6500, icon: '🐦',
        name: 'Gold Canary Figurine of Wondrous Power',
        category: 'items',
        description: 'Becomes a giant canary for 8 hours. Can be ridden as a mount with 60 ft fly speed.',
        requiresAttunement: false,
        rarity: 'rare',
        charges: 1,
        maxCharges: 1,
        rechargeType: 'weekly'
      },
      'items_hoard_magic_item': {
        weight: 5, size: [2, 2], baseValue: 50000, icon: '👑',
        name: 'Hoard Magic Item',
        category: 'items',
        description: 'An item from a dragon hoard. Roll on the hoard magic item table for properties.',
        requiresAttunement: true,
        rarity: 'legendary'
      },
      'items_luckstone_carved_from_hoard': {
        weight: 0.1, size: [1, 1], baseValue: 4200, icon: '🎲',
        name: 'Luckstone Carved from Hoard',
        category: 'items',
        description: '+1 bonus to ability checks and saving throws. Found in dragon hoards.',
        requiresAttunement: true,
        rarity: 'uncommon'
      },
      'items_orb_of_dragonkind': {
        weight: 3, size: [1, 1], baseValue: 210000, icon: '🔮',
        name: 'Orb of Dragonkind',
        category: 'items',
        description: 'Control dragons, gain darkvision, speak Draconic. Can summon dragons but at great risk.',
        equipSlot: 'mainhand',
        requiresAttunement: true,
        rarity: 'artifact',
        charges: 3,
        maxCharges: 3,
        chargesPerDay: true,
        rechargeType: 'dawn'
      },
      'items_platinum_scarf': {
        weight: 1, size: [2, 1], baseValue: 6000, icon: '🧣',
        name: 'Platinum Scarf',
        category: 'items',
        description: 'While wearing, immune to frightened condition. Can cast sanctuary once per day.',
        equipSlot: 'neck',
        requiresAttunement: true,
        rarity: 'legendary',
        spells: [
          { name: 'Sanctuary', charges: 1, level: 1 }
        ],
        charges: 1,
        maxCharges: 1,
        chargesPerDay: true,
        rechargeType: 'dawn'
      },
      'items_potion_of_dragon_majesty': {
        weight: 0.5, size: [1, 1], baseValue: 5000, icon: '🧪',
        name: 'Potion of Dragon Majesty',
        category: 'potions',
        description: 'For 1 hour, gain dragon-like presence. Advantage on Charisma checks and can speak Draconic.',
        rarity: 'legendary',
        charges: 1,
        maxCharges: 1,
        rechargeType: 'never'
      },
      'items_ruby_weave_gem': {
        weight: 0.1, size: [1, 1], baseValue: 30000, icon: '💎',
        name: 'Ruby Weave Gem',
        category: 'items',
        description: 'While holding, resistance to one damage type. Can cast teleportation circle once per day.',
        requiresAttunement: true,
        rarity: 'legendary',
        spells: [
          { name: 'Teleportation Circle', charges: 1, level: 5 }
        ],
        charges: 1,
        maxCharges: 1,
        chargesPerDay: true,
        rechargeType: 'dawn'
      },
      'items_sapphire_buckler': {
        weight: 6, size: [2, 2], baseValue: 16000, icon: '🛡️',
        name: 'Sapphire Buckler',
        category: 'armor',
        description: '+2 shield. Resistance to psychic damage and immunity to frightened condition.',
        acBonus: 2,
        armorType: 'shield',
        requiresAttunement: true,
        rarity: 'very rare',
        magicalBonus: 2
      },
      'items_scaled_ornament': {
        weight: 0.1, size: [1, 1], baseValue: 700, icon: '🐲',
        name: 'Scaled Ornament',
        category: 'items',
        description: 'Dragon scale jewelry. While wearing, proficiency bonus doubled for Charisma checks with dragons.',
        equipSlot: 'trinket',
        requiresAttunement: false,
        rarity: 'uncommon'
      },
      'items_stirring_dragon_vessel': {
        weight: 1, size: [1, 1], baseValue: 8000, icon: '🏺',
        name: 'Stirring Dragon Vessel',
        category: 'items',
        description: 'Enhanced dragon vessel. Can produce stronger potions and has additional breath weapon ability.',
        requiresAttunement: true,
        rarity: 'very rare',
        charges: 3,
        maxCharges: 3,
        chargesPerDay: true,
        rechargeType: 'dawn'
      },
      'items_tome_of_draconic_secrets': {
        weight: 5, size: [2, 2], baseValue: 50000, icon: '📖',
        name: 'Tome of Draconic Secrets',
        category: 'items',
        description: 'Study for 48 hours to increase Intelligence by 2 and maximum by 2. Contains ancient dragon knowledge.',
        equipSlot: 'mainhand',
        requiresAttunement: false,
        rarity: 'legendary'
      },
      'items_topaz_annihilator': {
        weight: 50, size: [3, 3], baseValue: 65000, icon: '💎',
        name: 'Topaz Annihilator',
        category: 'items',
        description: 'Magical cannon that fires necrotic blasts. Deals 8d8 necrotic damage in 60-foot cone.',
        requiresAttunement: false,
        rarity: 'legendary',
        charges: 10,
        maxCharges: 10,
        chargesPerDay: true,
        rechargeType: 'dawn'
      },
      'items_wakened_dragon_vessel': {
        weight: 1, size: [1, 1], baseValue: 16000, icon: '🏺',
        name: 'Wakened Dragon Vessel',
        category: 'items',
        description: 'The most powerful dragon vessel. Can transform user into young dragon form once per day.',
        requiresAttunement: true,
        rarity: 'legendary',
        charges: 1,
        maxCharges: 1,
        chargesPerDay: true,
        rechargeType: 'dawn'
      },
      'items_wyrm_claw_tattoo': {
        weight: 0, size: [1, 1], baseValue: 20000, icon: '🐉',
        name: 'Wyrm Claw Tattoo',
        category: 'items',
        description: 'Unarmed strikes deal extra 1d6 slashing and count as magical. +2 to unarmed strike attacks.',
        requiresAttunement: true,
        rarity: 'very rare',
        magicalBonus: 2
      },
      'items_wyrmreaver_gauntlets': {
        weight: 2, size: [1, 1], baseValue: 18000, icon: '🧤',
        name: 'Wyrmreaver Gauntlets',
        category: 'items',
        description: '+2 bonus to melee weapon attacks. Deal extra 2d6 damage to dragons.',
        equipSlot: 'hands',
        requiresAttunement: true,
        rarity: 'very rare',
        magicalBonus: 2
      }
    },
    spells: {
      // Fizban's Dragon Spells
      'ashardalons_stride': {
        name: "Ashardalon's Stride",
        level: 3,
        school: "Transmutation",
        castTime: "1 bonus action",
        range: "Self",
        components: "V, S",
        duration: "Concentration, up to 1 minute",
        description: "Speed increases by 20 feet, deal 1d6 fire damage to creatures you move within 5 feet of.",
        damage: "1d6",
        damageType: "fire",
        upcastDamage: "1d6",
        classes: ["Ranger", "Sorcerer", "Wizard"]
      },
      'draconic_transformation': {
        name: "Draconic Transformation",
        level: 7,
        school: "Transmutation",
        castTime: "1 bonus action",
        range: "Self",
        components: "V, S, M (a statuette of a dragon worth at least 500 gp)",
        duration: "Concentration, up to 1 minute",
        description: "Transform into a draconic form. Gain blindsight, flight, and breath weapon.",
        damage: "6d8",
        damageType: "force",
        saveType: "dexterity",
        classes: ["Druid", "Sorcerer", "Wizard"]
      },
      'draconic_spirit': {
        name: "Summon Draconic Spirit",
        level: 5,
        school: "Conjuration",
        castTime: "1 action",
        range: "60 feet",
        components: "V, S, M (an object with the image of a dragon worth at least 500 gp)",
        duration: "Concentration, up to 1 hour",
        description: "Summon a draconic spirit to fight alongside you.",
        classes: ["Druid", "Sorcerer", "Wizard"]
      },
      'fizban_platinum_shield': {
        name: "Fizban's Platinum Shield",
        level: 6,
        school: "Abjuration",
        castTime: "1 bonus action",
        range: "60 feet",
        components: "V, S, M (a platinum-plated dragon scale worth at least 500 gp)",
        duration: "Concentration, up to 1 minute",
        description: "Create a shield of shimmering platinum. Grant half cover, resistance to acid, cold, fire, lightning, poison.",
        classes: ["Sorcerer", "Wizard"]
      },
      'nathair_mischief': {
        name: "Nathair's Mischief",
        level: 2,
        school: "Illusion",
        castTime: "1 action",
        range: "60 feet",
        components: "S, M (a piece of crust from an apple pie)",
        duration: "Concentration, up to 1 minute",
        description: "Fill a 20-foot cube with fey and draconic magic that randomly affects creatures.",
        saveType: "varies",
        classes: ["Bard", "Sorcerer", "Wizard"]
      },
      'raulothims_psychic_lance': {
        name: "Raulothim's Psychic Lance",
        level: 4,
        school: "Enchantment",
        castTime: "1 action",
        range: "120 feet",
        components: "V",
        duration: "Instantaneous",
        description: "Lance of psychic energy. Target takes 7d6 psychic damage and is incapacitated.",
        damage: "7d6",
        damageType: "psychic",
        saveType: "intelligence",
        upcastDamage: "1d6",
        classes: ["Bard", "Sorcerer", "Warlock", "Wizard"]
      },
      'rime_binding_ice': {
        name: "Rime's Binding Ice",
        level: 2,
        school: "Evocation",
        castTime: "1 action",
        range: "Self (30-foot cone)",
        components: "S, M (a vial of water)",
        duration: "Instantaneous",
        description: "30-foot cone of ice. Creatures take 3d8 cold damage and speed reduced to 0.",
        damage: "3d8",
        damageType: "cold",
        saveType: "constitution",
        upcastDamage: "1d8",
        classes: ["Sorcerer", "Wizard"]
      },
      'summon_draconic_spirit': {
        name: "Summon Draconic Spirit",
        level: 5,
        school: "Conjuration",
        castTime: "1 action",
        range: "60 feet",
        components: "V, S, M (an object with the image of a dragon worth at least 500 gp)",
        duration: "Concentration, up to 1 hour",
        description: "Summon a draconic spirit in the form of a dragon that fights for you.",
        classes: ["Druid", "Sorcerer", "Wizard"]
      },
      'dragon_breath': {
        name: "Dragon's Breath",
        level: 2,
        school: "Transmutation",
        castTime: "1 bonus action",
        range: "Touch",
        components: "V, S, M (a hot pepper)",
        duration: "Concentration, up to 1 minute",
        description: "Grant a creature the ability to breathe elemental energy. 15-foot cone, 3d6 damage.",
        damage: "3d6",
        damageType: "varies",
        saveType: "dexterity",
        upcastDamage: "1d6",
        classes: ["Sorcerer", "Wizard"]
      },
      'chromatic_orb': {
        name: "Chromatic Orb",
        level: 1,
        school: "Evocation",
        castTime: "1 action",
        range: "90 feet",
        components: "V, S, M (a diamond worth at least 50 gp)",
        duration: "Instantaneous",
        description: "Hurl a sphere of energy. Choose acid, cold, fire, lightning, poison, or thunder. 3d8 damage.",
        damage: "3d8",
        damageType: "varies",
        saveType: "attack",
        upcastDamage: "1d8",
        classes: ["Sorcerer", "Wizard"]
      },
      'alter_self_dragon': {
        name: "Alter Self (Dragon Form)",
        level: 2,
        school: "Transmutation",
        castTime: "1 action",
        range: "Self",
        components: "V, S",
        duration: "Concentration, up to 1 hour",
        description: "Assume draconic features. Choose aquatic adaptation, change appearance, or natural weapons.",
        classes: ["Sorcerer", "Wizard"]
      },
      'illusory_dragon': {
        name: "Illusory Dragon",
        level: 8,
        school: "Illusion",
        castTime: "1 action",
        range: "120 feet",
        components: "S",
        duration: "Concentration, up to 1 minute",
        description: "Create illusion of Huge dragon. Can use action to make it breathe for 7d6 damage.",
        damage: "7d6",
        damageType: "varies",
        saveType: "intelligence",
        classes: ["Wizard"]
      }
    },
    // [PHASE 2.3 - NOV 18, 2025]: Converted all prerequisites from text to structured objects
    feats: {
      'gift_of_the_chromatic_dragon': {
        id: 'gift_of_the_chromatic_dragon',
        name: "Gift of the Chromatic Dragon",
        description: "You've manifested some of the power of chromatic dragons, granting you the following benefits.",
        source: "Fizban's Treasury of Dragons",
        prerequisites: null,
        benefits: [
          "As a bonus action, you can touch a simple or martial weapon and infuse it with one of the following damage types: acid, cold, fire, lightning, or poison. For the next minute, the weapon deals an extra 1d4 damage of the chosen type when it hits. After you use this bonus action, you can't do so again until you finish a long rest.",
          "When you take acid, cold, fire, lightning, or poison damage, you can use your reaction to give yourself resistance to that instance of damage. You can use this reaction a number of times equal to your proficiency bonus, and you regain all expended uses when you finish a long rest."
        ],
        // [PHASE 8 - NOV 18, 2025]: Full implementation with unified effects schema
        effects: [],
        activeAbilities: {
          chromaticInfusion: {
            name: 'Chromatic Infusion',
            description: 'Infuse a weapon with elemental damage (acid, cold, fire, lightning, or poison) for 1 minute',
            actionType: 'bonus',
            usesPerLongRest: 1,
            duration: '1 minute',
            damageTypes: ['acid', 'cold', 'fire', 'lightning', 'poison'],
            damageBonus: '1d4'
          },
          reactiveResistance: {
            name: 'Reactive Resistance',
            description: 'Gain resistance to acid, cold, fire, lightning, or poison damage as a reaction',
            actionType: 'reaction',
            usesPerLongRest: 'proficiency_bonus',
            damageTypes: ['acid', 'cold', 'fire', 'lightning', 'poison'],
            condition: {
              when: 'take_elemental_damage',
              check: (character, context) => ['acid', 'cold', 'fire', 'lightning', 'poison'].includes(context?.damageType)
            }
          }
        },
        tags: ['dragon', 'chromatic', 'offensive', 'defensive', 'reaction', 'elemental']
      },
      'gift_of_the_metallic_dragon': {
        id: 'gift_of_the_metallic_dragon',
        name: "Gift of the Metallic Dragon",
        description: "You've manifested some of the power of metallic dragons, granting you the following benefits.",
        source: "Fizban's Treasury of Dragons",
        prerequisites: null,
        benefits: [
          "You learn the cure wounds spell. You can cast this spell without expending a spell slot. Once you cast this spell in this way, you can't do so again until you finish a long rest. You can also cast this spell using spell slots you have. The spell's spellcasting ability is Intelligence, Wisdom, or Charisma when you cast it with this feat (choose when you gain the feat).",
          "You can manifest protective wings that can shield you or others. When you or another creature you can see within 5 feet of you is hit by an attack roll, you can use your reaction to manifest spectral wings from your back for a moment. You grant a bonus to the target's AC equal to your proficiency bonus against that attack roll, potentially causing it to miss. You can use this reaction a number of times equal to your proficiency bonus, and you regain all expended uses when you finish a long rest."
        ],
        // [PHASE 8 - NOV 18, 2025]: Full implementation with unified effects schema
        effects: [],
        customChoices: {
          spellcastingAbility: {
            choose: 1,
            from: ['int', 'wis', 'cha'],
            description: 'Choose spellcasting ability for cure wounds'
          }
        },
        activeAbilities: {
          castCureWounds: {
            name: 'Cast Cure Wounds',
            description: 'Cast cure wounds once per long rest without expending a spell slot',
            usesPerLongRest: 1,
            actionType: 'action'
          },
          protectiveWings: {
            name: 'Protective Wings',
            description: 'Grant AC bonus equal to proficiency bonus to yourself or ally within 5 feet',
            actionType: 'reaction',
            usesPerLongRest: 'proficiency_bonus',
            range: 5,
            acBonus: 'proficiency_bonus',
            condition: {
              when: 'ally_hit_by_attack',
              check: (character, context) => context?.wasHit === true && context?.distance <= 5
            }
          }
        },
        tags: ['dragon', 'metallic', 'healing', 'defensive', 'reaction', 'protection']
      },
      'gift_of_the_gem_dragon': {
        id: 'gift_of_the_gem_dragon',
        name: "Gift of the Gem Dragon",
        description: "You've manifested some of the power of gem dragons, granting you the following benefits.",
        source: "Fizban's Treasury of Dragons",
        prerequisites: null,
        benefits: [
          "Increase your Intelligence, Wisdom, or Charisma score by 1, to a maximum of 20.",
          "When you take damage from a creature that is within 10 feet of you, you can use your reaction to emanate telekinetic energy. The creature that dealt damage to you must succeed on a Strength saving throw (DC equals 8 + your proficiency bonus + the ability modifier of the score increased by this feat) or take 2d8 force damage and be pushed 10 feet away from you. You can use this reaction a number of times equal to your proficiency bonus, and you regain all expended uses when you finish a long rest."
        ],
        // [PHASE 8 - NOV 18, 2025]: Full implementation with unified effects schema
        effects: [],
        abilityScoreIncrease: {
          choice: [
            { int: 1 },
            { wis: 1 },
            { cha: 1 }
          ]
        },
        activeAbilities: {
          telekineticReprisal: {
            name: 'Telekinetic Reprisal',
            description: 'Deal 2d8 force damage and push attacker 10 feet (Strength save)',
            actionType: 'reaction',
            usesPerLongRest: 'proficiency_bonus',
            range: 10,
            damage: '2d8',
            damageType: 'force',
            saveType: 'strength',
            saveDC: '8_plus_prof_plus_ability',
            pushDistance: 10,
            condition: {
              when: 'take_damage_from_nearby',
              check: (character, context) => context?.tookDamage === true && context?.distance <= 10
            }
          }
        },
        tags: ['dragon', 'gem', 'offensive', 'defensive', 'reaction', 'force', 'telekinetic']
      },
      'dragon_hide_feat': {
        id: 'dragon_hide_feat',
        name: "Dragon Hide",
        description: "You manifest scales and claws reminiscent of your draconic ancestors. You gain the following benefits.",
        source: "Xanathar's Guide to Everything / Fizban's Treasury of Dragons",
        prerequisites: {
          race: 'dragonborn',
          description: "Dragonborn"
        },
        benefits: [
          "Increase your Strength, Constitution, or Charisma score by 1, to a maximum of 20.",
          "Your scales harden. While you aren't wearing armor, you can calculate your AC as 13 + your Dexterity modifier. You can use a shield and still gain this benefit.",
          "You grow retractable claws from the tips of your fingers. Extending or retracting the claws requires no action. The claws are natural weapons, which you can use to make unarmed strikes. If you hit with them, you deal slashing damage equal to 1d4 + your Strength modifier, instead of the bludgeoning damage normal for an unarmed strike."
        ],
        // [PHASE 8 - NOV 18, 2025]: Full implementation (duplicate from Xanathar's, also in Fizban's)
        effects: [
          {
            type: 'ac',
            operation: 'set',
            value: '13_plus_dex',
            condition: {
              when: 'not_wearing_armor',
              check: (character) => !character?.equipment?.armor
            }
          },
          {
            type: 'unarmedDamage',
            operation: 'set',
            value: '1d4',
            damageType: 'slashing',
            condition: 'always'
          }
        ],
        abilityScoreIncrease: {
          choice: [
            { str: 1 },
            { con: 1 },
            { cha: 1 }
          ]
        },
        tags: ['dragonborn', 'defensive', 'offensive', 'unarmed', 'ac', 'xanathars', 'fizbans']
      },
      'dragon_fear_feat': {
        id: 'dragon_fear_feat',
        name: "Dragon Fear",
        description: "When angered, you can radiate menace. You gain the following benefits.",
        source: "Xanathar's Guide to Everything / Fizban's Treasury of Dragons",
        prerequisites: {
          race: 'dragonborn',
          description: "Dragonborn"
        },
        benefits: [
          "Increase your Strength, Constitution, or Charisma score by 1, to a maximum of 20.",
          "Instead of exhaling destructive energy, you can expend a use of your Breath Weapon trait to roar, forcing each creature of your choice within 30 feet of you to make a Wisdom saving throw (DC 8 + your proficiency bonus + your Charisma modifier). A target automatically succeeds on the save if it can't hear or see you. On a failed save, a target becomes frightened of you for 1 minute. If the frightened target takes any damage, it can repeat the saving throw, ending the effect on itself on a success."
        ],
        // [PHASE 8 - NOV 18, 2025]: Full implementation (duplicate from Xanathar's, also in Fizban's)
        effects: [],
        abilityScoreIncrease: {
          choice: [
            { str: 1 },
            { con: 1 },
            { cha: 1 }
          ]
        },
        activeAbilities: {
          fearfulRoar: {
            name: 'Fearful Roar',
            description: 'Expend Breath Weapon to frighten creatures within 30 feet (Wisdom save)',
            actionType: 'action',
            range: 30,
            saveType: 'wisdom',
            saveDC: '8_plus_prof_plus_cha',
            duration: '1 minute',
            condition: 'frightened',
            usesBreathWeapon: true
          }
        },
        tags: ['dragonborn', 'control', 'fear', 'breath_weapon', 'xanathars', 'fizbans']
      }
    },
    abilities: {
      // Draconic Subclasses and Options
      'drakewarden': {
        name: "Drakewarden",
        description: "Ranger: Bond with a drake companion that grows stronger as you level.",
        class: "Ranger",
        level: 3
      },
      'way_of_the_ascendant_dragon': {
        name: "Way of the Ascendant Dragon",
        description: "Monk: Channel draconic ki, gain breath weapon, and eventually sprout dragon wings.",
        class: "Monk",
        level: 3
      },
      'circle_of_wildfire_dragon': {
        name: "Circle of Wildfire (Dragon Focus)",
        description: "Druid: Summon wildfire spirit with draconic features and enhanced fire abilities.",
        class: "Druid",
        level: 2
      },
      'college_of_dragons': {
        name: "College of Dragons",
        description: "Bard: Learn draconic secrets, inspire with dragon tales, and master dragon magic.",
        class: "Bard",
        level: 3
      },
      'dragon_ancestor': {
        name: "Dragon Ancestor",
        description: "Sorcerer: Enhanced draconic bloodline features with additional dragon types including gem dragons.",
        class: "Sorcerer",
        level: 1
      },
      'pact_of_the_dragon': {
        name: "Pact of the Dragon",
        description: "Warlock: Form a pact with an ancient dragon, gaining draconic spells and invocations.",
        class: "Warlock",
        level: 1
      },
      'dragon_disciple': {
        name: "Dragon Disciple",
        description: "Wizard: Specialize in dragon magic, gaining ability to speak Draconic and resist dragon fear.",
        class: "Wizard",
        level: 2
      },
      'breath_weapon_mastery': {
        name: "Breath Weapon Mastery",
        description: "Dragonborn: Your breath weapon recharges on a short rest instead of long rest.",
        class: "Dragonborn",
        level: 5
      },
      'dragon_wings': {
        name: "Dragon Wings",
        description: "Dragonborn variant: Manifest dragon wings, gaining a flying speed equal to walking speed.",
        class: "Dragonborn",
        level: 5
      },
      'gem_ancestry': {
        name: "Gem Ancestry",
        description: "Dragonborn variant: Descended from gem dragons with psychic, radiant, or force damage types.",
        class: "Dragonborn",
        level: 1
      },
      'metallic_breath_weapon': {
        name: "Metallic Breath Weapon",
        description: "Dragonborn: Gain a second breath weapon that incapacitates instead of dealing damage.",
        class: "Dragonborn",
        level: 1
      },
      'chromatic_warding': {
        name: "Chromatic Warding",
        description: "Dragonborn: Gain immunity to your ancestry's damage type for 1 minute once per long rest.",
        class: "Dragonborn",
        level: 5
      },
      'dragon_sight': {
        name: "Dragon Sight",
        description: "Gain blindsight out to 10 feet and darkvision out to 60 feet.",
        class: "Any",
        level: 10
      },
      'frightful_presence': {
        name: "Frightful Presence",
        description: "As an action, creatures within 30 feet must make Wisdom save or be frightened.",
        class: "Any",
        level: 17
      },
      'legendary_resistance': {
        name: "Legendary Resistance",
        description: "Once per day, if you fail a saving throw, you can choose to succeed instead.",
        class: "Any",
        level: 20
      }
    },
    // [FIX JAN 2026]: Added proper subclasses section - was previously only in abilities
    subclasses: {
      'drakewarden': {
        name: 'Drakewarden',
        class: 'Ranger',
        source: 'fizbans',
        level: 3,
        description: 'Your connection to the natural world takes the form of a draconic spirit, which can manifest as a drake companion. As your powers grow, your drake companion takes on more and more draconic characteristics.',
        features: {
          3: ['Draconic Gift', 'Drake Companion'],
          7: ['Bond of Fang and Scale'],
          11: ["Drake's Breath"],
          15: ['Perfected Bond']
        }
      },
      'way_of_the_ascendant_dragon': {
        name: 'Way of the Ascendant Dragon',
        class: 'Monk',
        source: 'fizbans',
        level: 3,
        description: 'The fundamental teaching of this tradition holds that by emulating dragons, a monk becomes a more integrated part of the world and its magic. By altering their spirit to resonate with draconic might, monks gain the ability to channel elements and awaken the dragon within.',
        features: {
          3: ['Draconic Disciple', 'Breath of the Dragon'],
          6: ['Wings Unfurled'],
          11: ['Aspect of the Wyrm'],
          17: ['Ascendant Aspect']
        }
      }
    },
    backgrounds: {},
    boons: {}
  }
};